import { useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

export default function Footer() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get("website_alt") as string) || "";
    if (honeypot.trim().length > 0) {
      setStatus("success");
      form.reset();
      return;
    }
    data.delete("website_alt");
    const email = (data.get("email") as string) || "";
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setErrorMsg("請輸入有效的電子郵件地址");
      return;
    }
    setStatus("loading");
    try {
      const params = new URLSearchParams();
      data.forEach((v, k) => params.append(k, String(v)));
      const res = await fetch("https://readdy.ai/api/form/dack6dhtto0ceqqng470", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(text);
      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(serverMsg || "訂閱失敗，請稍後再試");
      }
    } catch {
      setStatus("error");
      setErrorMsg("網路錯誤，請稍後再試");
    }
  };

  return (
    <footer className="bg-[#f4f8f5] pt-16 pb-10 border-t border-brand-100">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-1">
          <div className="inline-block p-3 rounded-xl bg-white/80 border border-brand-100/60 shadow-sm">
            <img
              src="https://storage.helloreaddy.io/project_files/5083fe3c-0446-4f05-ab30-834aed56ded7/6a623ee9-74ef-4c4d-9fa4-01d9417285e9_compressed_logo5.webp"
              alt="ISHB Logo"
              className="h-12 md:h-14 w-auto object-contain"
            />
          </div>
          <p className="mt-6 text-sm text-brand-900/60">© 2026</p>
          <p className="text-sm text-brand-900/60">All right reserved.</p>
        </div>

        <div>
          <h4 className="text-brand-900 font-semibold mb-4"><Link to="/#philosophy" className="cursor-pointer">About</Link></h4>
          <ul className="space-y-2 text-sm text-brand-900/70">
            <li><Link to="/team" className="hover:text-brand-700 cursor-pointer">專業團隊</Link></li>
            <li><Link to="/franchise" className="hover:text-brand-700 cursor-pointer">成為加盟商</Link></li>
            <li><Link to="/treatments" className="hover:text-brand-700 cursor-pointer">療程資訊</Link></li>
            <li><Link to="/courses" className="hover:text-brand-700 cursor-pointer">課程介紹</Link></li>
            <li><Link to="/#philosophy" className="hover:text-brand-700 cursor-pointer">本校理念</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-brand-900 font-semibold mb-4">Explore</h4>
          <ul className="space-y-2 text-sm text-brand-900/70">
            <li><Link to="/articles" className="hover:text-brand-700 cursor-pointer">專業文章</Link></li>
            <li><Link to="/cases" className="hover:text-brand-700 cursor-pointer">客人見證</Link></li>
            <li><Link to="/faq" className="hover:text-brand-700 cursor-pointer">常見問答</Link></li>
            <li><Link to="/courses" className="hover:text-brand-700 cursor-pointer">最新課程</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-brand-900 font-semibold mb-4">Contact</h4>
          <ul className="space-y-2 text-sm text-brand-900/70">
            <li className="flex items-center gap-2"><i className="ri-phone-line text-brand-600"></i> +852 6943 8757</li>
            <li className="flex items-center gap-2"><i className="ri-whatsapp-line text-brand-600"></i> +852 6943 8757</li>
            <li className="flex items-start gap-2"><i className="ri-map-pin-line text-brand-600 mt-0.5"></i> 尖沙咀金馬倫道 10 號宏威商業大廈 7 樓全層</li>
          </ul>
        </div>

        <div id="newsletter">
          <h4 className="text-brand-900 font-semibold mb-4">取得最新資訊與優惠！</h4>
          <form
            id="newsletter-form"
            ref={formRef}
            onSubmit={onSubmit}
            data-readdy-form
            className="relative"
          >
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="hp-honeypot"
            />
            <div className="flex items-center border-b border-brand-300">
              <input
                type="email"
                name="email"
                required
                placeholder="YOUR EMAIL"
                className="flex-1 bg-transparent py-2 text-sm placeholder:text-brand-900/40 focus:outline-none"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 text-white cursor-pointer transition"
                aria-label="subscribe"
              >
                <i className={`ri-${status === "loading" ? "loader-4-line animate-spin" : "arrow-right-line"}`}></i>
              </button>
            </div>
            {status === "success" && (
              <p className="mt-2 text-xs text-brand-600">訂閱成功，感謝您！</p>
            )}
            {status === "error" && (
              <p className="mt-2 text-xs text-rose-500">{errorMsg}</p>
            )}
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12 pt-6 border-t border-brand-100 flex flex-col sm:flex-row items-center justify-between text-xs text-brand-900/50">
        <span>ISHB 國際病變皮膚大健康及美業學會</span>
        <div className="flex items-center gap-4 mt-3 sm:mt-0">
          <a href="#" className="hover:text-brand-700 cursor-pointer"><i className="ri-facebook-fill"></i></a>
          <a href="#" className="hover:text-brand-700 cursor-pointer"><i className="ri-instagram-line"></i></a>
          <a href="#" className="hover:text-brand-700 cursor-pointer"><i className="ri-youtube-line"></i></a>
          <a href="#" className="hover:text-brand-700 cursor-pointer"><i className="ri-wechat-line"></i></a>
        </div>
      </div>
    </footer>
  );
}